import { Box, InputBase, Typography, useTheme } from "@mui/material";
import { useEffect, useRef } from "react";

interface OnboardingOtherInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: boolean;
  onErrorClear?: () => void;
}

export const OnboardingOtherInput = ({
  value,
  onChange,
  placeholder = "Please specify",
  error,
  onErrorClear,
}: OnboardingOtherInputProps): JSX.Element => {
  const theme = useTheme();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
    return () => clearTimeout(timer);
  }, []);

  const handleChange = (val: string): void => {
    onChange(val);
    if (error && onErrorClear) {
      onErrorClear();
    }
  };

  return (
    <Box data-other-error={error || undefined}>
      <InputBase
        inputRef={inputRef}
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        placeholder={placeholder}
        fullWidth
        sx={{
          height: "32px",
          padding: "0 8px",
          borderRadius: "4px",
          border: "1px solid",
          borderColor: error
            ? theme.palette.error.main
            : theme.palette.primitive.neutrals.neutral[800],
          color: theme.palette.primitive.neutrals.neutral["600"],
          transition: "border-color 0.2s ease",
          "&.Mui-focused": {
            borderColor: error
              ? theme.palette.error.main
              : theme.palette.secondary["purple-tint"],
          },
          "& input": {
            ...theme.typography["body-small"],
            color: theme.palette.neutral.main,
            padding: 0,
            height: "100%",
            "&::placeholder": {
              color: theme.palette.primitive.neutrals.neutral[600],
              opacity: 1,
            },
          },
        }}
      />
      {error && (
        <Typography
          variant="body-small"
          sx={{ color: theme.palette.error.main, mt: "4px" }}
        >
          Please fill the details
        </Typography>
      )}
    </Box>
  );
};
